import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useState } from 'react';

import Button from '@/components/global/Button';
import { Column, Grid } from '@/components/global/Grid';
import Icon from '@/components/global/Icon';
import { DEFAULT_ARROW_ICON_SIZES } from '@/components/global/Icon/defaultIconSizes';
import { Pagination } from '@/components/global/Pagination';
import { useRegions } from '@/components/providers/RegionsProvider';
import { generateS3PathFromVariants } from '@/lib/fonts/s3FontPath';
import { usePaths } from '@/lib/paths';
import { getFontDisplayName, getProductFontSizeAdjust } from '@/lib/productHelpers';
import { useCustomiserCollectionLazyQuery } from '@/saleor/api';

import styles from './Proofer.module.scss';
import ProoferCard from './ProoferCard';

const ProoferShell = ({ totalCount, fontFamilies }) => {
  return (
    <>
      {[...Array(totalCount - fontFamilies?.length)].map((e, i) => (
        <div
          className={clsx(styles.Proofer__gridItem, styles['Proofer__gridItem--loading'])}
          key={i}
        >
          <div />
        </div>
      ))}
    </>
  );
};

const Proofer = ({ serverFontFamilies, categoryId }) => {
  const { query } = useRegions();
  const paths = usePaths();
  const router = useRouter();
  const [selectedFontFamily, setSelectedFontFamily] = useState(null);
  const DEFAULT_FILTERS = { categories: [categoryId], search: '' };
  const [filters, setFilters] = useState<any>(DEFAULT_FILTERS);
  const [familiesData, setFamiliesData] = useState<any>();
  const familiesRenderData = useCallback(
    () => familiesData || serverFontFamilies,
    [familiesData, serverFontFamilies]
  );

  const [getProducts, { loading }] = useCustomiserCollectionLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      setFamiliesData({
        products: result?.products
      });
    }
  });

  const onLoadMore = async (cursor) => {
    if (loading) {
      return;
    }

    await getProducts({
      variables: {
        filter: filters,
        first: 10,
        after: cursor,
        ...query
      }
    });
  };

  const dataFontFamilies = familiesRenderData()?.products?.edges.map((edge) => edge.node) || [];
  const pageInfo = familiesRenderData()?.products?.pageInfo;
  const totalCount = familiesRenderData()?.products?.totalCount || 0;

  useMemo(() => {
    getProducts({
      variables: {
        filter: filters,
        first: 100,
        after: null,
        ...query
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getProducts]);

  return (
    <Grid className={styles.CustomiserResult__grid}>
      <Column sm={12} md={12} lg={12}>
        <section className={styles.Proofer}>
          <div className={styles.Proofer__Header}>
            <h1 className={styles.Proofer__Heading}>Select a Font Family to Test</h1>
          </div>
          <div className={styles.Proofer__main}>
            <div className={styles.Proofer__wrapper}>
              <div className={styles.Proofer__grid} role="radiogroup" aria-label="Font families">
                {dataFontFamilies?.length > 0
                  ? dataFontFamilies.map((fontFamily) => {
                      const { name, slug, variants, attributes, metadata } = fontFamily;
                      const fontDisplayName = getFontDisplayName({ name, metadata });
                      const fontSizeAdjust = getProductFontSizeAdjust(fontFamily);

                      return (
                        <div
                          key={name}
                          className={styles.Proofer__gridItem}
                          style={
                            {
                              ...(fontSizeAdjust && { '--font-size-adjust': fontSizeAdjust })
                            } as React.CSSProperties
                          }
                        >
                          <ProoferCard
                            fontName={slug}
                            s3Path={generateS3PathFromVariants(slug, variants, attributes)}
                            setSelectedFontFamily={setSelectedFontFamily}
                          >
                            <span className={styles.fontName} style={{ fontFamily: slug }}>
                              {fontDisplayName.replace(/<br\s*\/?>/gi, '\n')}
                            </span>
                          </ProoferCard>
                        </div>
                      );
                    })
                  : 'No results'}
                {loading && (
                  <ProoferShell totalCount={totalCount} fontFamilies={dataFontFamilies} />
                )}
              </div>
            </div>
            <Pagination onLoadMore={onLoadMore} pageInfo={pageInfo} loadOnScroll />
          </div>
          <div className={clsx([styles.Proofer__Controls])}>
            <div className={styles.Proofer__ControlsInner}>
              <div></div>
              <div className={styles.Proofer__ControlsButtonContainer}>
                <Button
                  type="button"
                  className={clsx(styles.Proofer__Button)}
                  variant="primary"
                  ariaLabel="Take me to proofer"
                  size="xl"
                  callBack={() =>
                    router.push(
                      paths.typefaces._slug(selectedFontFamily).proofer._fontStyle().$url()
                    )
                  }
                  disabled={!selectedFontFamily}
                >
                  Take me to proofer →
                </Button>
              </div>
            </div>
          </div>
        </section>
      </Column>
    </Grid>
  );
};

export default Proofer;
